import { IonApp, IonCol, IonContent, IonPage, IonSpinner } from "@ionic/react";
import Cross from "assets/images/icon/svg/Cross";
import BackButtonTitle from "components/mobile-components/Common/BackButtonTitle";
import { COUNTRYINFO } from "data/country";
import { dateTimeFormatter, numberWithCommas } from "helpers/helper";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getSelectedDate } from "redux/container/dashboardSlice";
import Accounts from "services/Accounts";

const TotalTaxPage = () => {
  const ekkLanguage = useSelector((state) => state.lan.lan);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const barRef = useRef();
  const location = useLocation();
  const createUserData = useSelector((state) => state.user.createUserData);
  const dateFormat = useSelector((state) => state.date.format);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const searchParams = new URLSearchParams(location?.search);
  const [getDate, setGetDate] = useState({
    start: searchParams.get("start")
      ? moment(searchParams.get("start")).format("YYYY-MM-DD")
      : moment().startOf("month").format("YYYY-MM-DD"),
    end: searchParams.get("end")
      ? moment(searchParams.get("end")).format("YYYY-MM-DD")
      : moment().endOf("month").format("YYYY-MM-DD"),
    level: "month",
  });
  const taxBarRef = useRef();
  const [taxData, setTaxData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [totalTableAmount, setTotalTableAmount] = useState(0);
  const [currency, setCurrency] = useState("$");
  const [filterClicked, setFilterClicked] = useState(false);
  const chartData = {
    labels: taxData?.map((item) =>
      moment(item?.month, "MM-YYYY").format("MMM/YY")
    ),
    datasets: [
      {
        data: taxData?.map((item) => item?.total_tax),
        backgroundColor: "#FF3B30",
      },
    ],
  };

  const chartOptions = {
    barPercentage: 0.6,
    plugins: {
      legend: false,
    },
  };

  useEffect(() => {
    const { start, end } = getDate;
    if (!start || !end) return;
    fetchTaxTableData(start, end);
  }, [getDate]);

  useEffect(() => {
    const fetchTaxChartData = async () => {
      setLoading(true);
      const response = await Accounts.getTaxData(timeZone);
      if (response.status === 200) {
        setTaxData(response?.data);
      } else {
        setTaxData([]);
      }
      setLoading(false);
    };
    fetchTaxChartData();
  }, [timeZone]);

  const fetchTaxTableData = async (start, end) => {
    setTableLoading(true);
    const response = await Accounts.getTaxDetails(start, end);
    if (response.status === 200) {
      setTableData(response.results);
      setTotalTableAmount(response.total_amount);
      setCurrency(response?.currency);
    } else {
      setTableData([]);
    }
    setTableLoading(false);
  };

  const barClickHandler = async (event) => {
    const element = getElementAtEvent(barRef.current, event);
    if (!element.length) return;

    const elementIndex = element[0].index;
    const date = chartData?.labels[elementIndex];
    const value = chartData?.datasets[0].data[elementIndex];
    if (value === 0) return;

    const startTime = moment(date, "MMM/YY")
      .startOf("month")
      .format("YYYY-MM-DD");
    const endTime = moment(date, "MMM/YY").endOf("month").format("YYYY-MM-DD");

    const dateTime = { start: startTime, end: endTime, level: "month" };

    dispatch(getSelectedDate(dateTime));
    setGetDate(dateTime);
    fetchTaxTableData(startTime, endTime);
  };

  const handleCrossFilter = () => {
    let date = {
      value: "month",
      label: ekkLanguage.dashboard.thisMonth,
      name: "This Month",
    };
    let timeDate = dateTimeFormatter(date?.value);
    let dateTime = { ...timeDate, level: date?.name };
    setGetDate({
      start: moment(timeDate?.start).format("YYYY-MM-DD"),
      end: moment(timeDate?.end).format("YYYY-MM-DD"),
    });
    dispatch(getSelectedDate(dateTime));
    setFilterClicked(false);
  };

  return (
    <IonApp>
      <IonPage>
        <IonContent>
          <BackButtonTitle
            title="Revenue Tax"
            filter={true}
            setGetDate={setGetDate}
            setFilterClicked={setFilterClicked}
            filterClicked={filterClicked}
          />
          <div className="pt-14 px-4">
            {!filterClicked && (
              <div className="chart-wrapper relative mb-4">
                {loading && (
                  <div className="absolute h-full w-full flex justify-center items-center left-[7%] top-[5%]">
                    <IonSpinner name="circular" />
                  </div>
                )}
                <div className="px-2 pb-1 flex justify-between items-center pt-5">
                  <h4 className="font-bold text-18 sm:text-20 text-black-1000 mb-2">
                    Total Revenue Tax Collected
                  </h4>
                </div>
                <div
                  className="flex justify-center px-2"
                  style={{ width: "100%", maxWidth: "768px" }}
                >
                  <Bar
                    data={chartData}
                    options={chartOptions}
                    ref={barRef}
                    onClick={barClickHandler}
                  />
                </div>
              </div>
            )}

            {getDate && (
              <div className="mt-6 px-2">
                <div className="flex items-center">
                  {" "}
                  <h4 className="font-bold text-16 text-black-1000">
                  Revenue Tax on{" "}
                    <span>
                      <span>
                        {moment(getDate?.start, '"YYYY-MM-DD"').format(
                          dateFormat
                        )}{" "}
                        -{" "}
                        {moment(getDate?.end, "YYYY-MM-DD").format(dateFormat)}
                      </span>
                    </span>
                  </h4>
                  {filterClicked && (
                    <div
                      onClick={handleCrossFilter}
                      className="ml-1 p-1 bg-white border rounded-full shadow-xl cursor-pointer"
                    >
                      <Cross color={"black"} />
                    </div>
                  )}
                </div>

                <h4 className="font-bold text-16 text-black-1000 mb-4">
                  Total Revenue Tax Amount: {currency}
                  {numberWithCommas(totalTableAmount)}
                </h4>

                {tableLoading ? (
                  <div className="flex justify-center items-center h-20">
                    <IonSpinner name="dots" />
                  </div>
                ) : tableData.length > 0 ? (
                  <table
                    className="table-auto w-full bg-white"
                    style={{
                      boxShadow: "0px 3px 20px 0px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <thead>
                      <tr className="border h-[45px] border-r-0 border-l-0 border-D9D9D9 px-4">
                        <th className="border p-1 font-bold text-12">Date</th>
                        <th className="border p-1 font-bold text-14">Amount</th>
                        <th className="border p-1 font-bold text-14">
                          Category
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((el, i) => (
                        <tr
                          key={i}
                          className="text-center h-[34px] border border-r-0 border-l-0 border-D9D9D9 px-4 cursor-pointer"
                          onClick={() =>
                            navigate(
                              `/transaction-details/${el?.id}?from=dashboard`
                            )
                          }
                        >
                          <td className="border p-2 font-semibold text-14">
                            {moment(el?.date).format(dateFormat)}
                          </td>
                          <td className="border font-semibold p-2 text-14">
                            {currency}
                            {numberWithCommas(el?.tax)}
                          </td>
                          <td className="border font-semibold p-2 text-14">
                            {el?.acc_category?.title}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <IonCol size="12">
                    <div
                      className="text-center flex items-center justify-center"
                      style={{ height: "calc(100vh - 420px)" }}
                    >
                      <div>
                        <img
                          className="m-auto mb-[10px] mt-5"
                          src="/assets/images/not-found-file.png"
                          alt="emptyCard"
                        />
                        <p className="text-16 font-normal text-222222 mb-[35px]">
                          {ekkLanguage?.orderList?.notFound}
                        </p>
                      </div>
                    </div>
                  </IonCol>
                )}
              </div>
            )}
          </div>
        </IonContent>
      </IonPage>
    </IonApp>
  );
};

export default TotalTaxPage;
